<template>
  <div>
    <div class="row">
      <div class="col-xs-12 col-md-4">
        <div class="panel panel-default">
          <div class="panel-body pbn">
            <div class="form-group" id="labelContainer">
              <label for="label" class="strong">{{ lang.get('scoring-criteria.form.label.label') }}</label>
              <helpIcon :content="lang.get('scoring-criteria.form.label.help')"></helpIcon>
              <multilingual mode="textarea" :resource="resource" property="label" :supported-languages="supportedLanguages" @input="handleMultilingualInput"></multilingual>
            </div>
  
            <div class="form-group form-group-subordinate">
              <label for="title" >{{ lang.get('scoring-criteria.form.title.label') }}</label>
              <helpIcon :content="lang.get('scoring-criteria.form.title.help')"></helpIcon>
              <multilingual mode="text" :resource="resource" property="title" :supported-languages="supportedLanguages" :max-length="32" @input="handleMultilingualInput"></multilingual>
            </div>
  
            <div class="form-group form-group-subordinate">
              <label for="shortcode" >{{ lang.get('scoring-criteria.form.shortcode.label') }}</label>
              <helpIcon :content="lang.get('scoring-criteria.form.shortcode.help')"></helpIcon>
              <multilingual mode="text" :resource="resource" property="shortcode" :supported-languages="supportedLanguages" @input="handleMultilingualInput"></multilingual>
            </div>
  
            <div class="form-group form-group-subordinate" id="hintTextContainer">
              <label for="hintText" >{{ lang.get('scoring-criteria.form.hint_text.label') }}</label> <span class="optional-field">
                {{ lang.get('miscellaneous.optional') }}</span>
              <helpIcon :content="lang.get('scoring-criteria.form.hint_text.help')"></helpIcon>
              <multilingual
                  mode="markdown"
                  :resource="resource"
                  property="hintText"
                  :supported-languages="supportedLanguages"
                  mark-down-mode="full"
                  @input="handleMultilingualInput"
              ></multilingual>
            </div>
  
            <div class="form-group form-group-subordinate" id="helpTextContainer">
              <label for="helpText" >{{ lang.get('scoring-criteria.form.help_text.label') }}</label> <span class="optional-field">
                {{ lang.get('miscellaneous.optional') }}</span>
              <helpIcon :content="lang.get('scoring-criteria.form.help_text.help')"></helpIcon>
              <multilingual mode="textarea" :resource="resource" property="helpText" :supported-languages="supportedLanguages" @input="handleMultilingualInput"></multilingual>
            </div>
          </div>
        </div>
        <div class="panel panel-default">
          <div class="panel-body pbn">
            <div class="form-group">
              <category-config
                  :resource="criterion"
                  :categories="categories"
                  :select-categories-label="lang.get('scoring-criteria.form.categories_select.label')"
                  :all-categories-label="lang.get('scoring-criteria.form.categories_all.label')"
                  @input="onCategorySelected"
              >
              </category-config>
            </div>
  
            <div class="form-group">
              <label for="fieldId">{{ lang.get('scoring-criteria.form.field.label') }}</label> <span class="optional-field">
                {{ lang.get('miscellaneous.optional') }}</span>
              <helpIcon :content="lang.get('scoring-criteria.form.field.help')"></helpIcon>
              <select class="form-control" name="fieldId" id="fieldId" v-model="selectedFieldId">
                <option>&nbsp;</option>
                <option v-for="field in fields" v-if="showField(field)" :value="field.id" :selected="field.id === criterion.fieldId">
                  {{ field.title }}
                </option>
              </select>
  
              <div v-if="selectedField && selectedField.conditional" class="alert-warning sticky" style="margin-top: 20px;">
                <div class="message">{{ lang.get('scoring-criteria.form.field.conditional-warning') }}</div>
              </div>
            </div>
  
            <div v-if="selectedField" class="form-group">
              <div class="checkbox">
                <label><input type="checkbox" name="ignoreEmpty" :checked="criterion.ignoreEmpty && !selectedField.content" :disabled="selectedField.content" /> {{ lang.get('scoring-criteria.form.ignore_empty.label') }}</label>
              </div>
            </div>
            
            <div class="form-group">
              <label for="order">{{ lang.get('scoring-criteria.form.order.label') }}</label> <span class="optional-field">
                {{ lang.get('miscellaneous.optional') }}</span>
              <helpIcon :content="lang.get('scoring-criteria.form.help_text.help')"></helpIcon>
              <input id="order" type="number" name="order" v-model="criterion.order" min="0" class="form-control"/>
            </div>
          </div>
        </div>
      </div>

      <input type="hidden" name="seasonId" :value="criterion.seasonId" />
      
      <div class="col-xs-12 col-md-4">
        <div :class="['form-group', {error: hasError('scoresetId')}]">
          <label>
            <span v-if="criterion.hasScores"  class="af-icons af-icons-lock"></span>
            {{ lang.get('scoring-criteria.form.score_set.label') }}
            <helpIcon v-if="criterion.hasScores"  :content="lang.get('scoring-criteria.form.score_set.has_scores')"></helpIcon>
            <input type="hidden" name="scoresetId" v-model="criterion.scoresetId">
          </label>
          <score-set-selector
            id="scoresetId"
            name="scoresetId"
            :score-sets="scoreSets"
            :disabled="criterion.hasScores"
            :current-score-set="scoresetId"
            :redirect-on-select="false"
            @change="(v) => scoresetId = v"
            >
          </score-set-selector>
  
        </div>
  
        <div :class="['form-group', {error: hasError('maximumScore')}]">
          <label for="maximumScore"> {{ lang.get('scoring-criteria.form.maximum_score.label') }}</label>
          <input id="maximumScore" type="number" class="form-control" name="maximumScore" v-model="maximumScore" @input="$emit('input', )"/>
        </div>
  
        <div :class="['form-group', {error: hasError('minimumScore')}]">
          <label for="minimumScore"> {{ lang.get('scoring-criteria.form.minimum_score.label') }}</label>
          <input id="minimumScore" type="number" class="form-control" name="minimumScore" v-model="minimumScore"/>
        </div>
  
        <div :class="['form-group', {error: hasError('weight')}]">
          <label for="weight"> {{ lang.get('scoring-criteria.form.weight.label') }}</label>
          <helpIcon :content="lang.get('scoring-criteria.form.weight.help')"></helpIcon>
          <input id="weight" name="weight" v-model="weight" min="0" class="form-control"/>
        </div>
  
        <div :class="['form-group', {error: hasError('scoringControl')}]">
          {{ lang.get('scoring-criteria.form.scoring_control.label') }}
          <div class="radio">
            <label for="selectScoringControl">
              <input type="radio" id="selectScoringControl" name="scoringControl" value="select" v-model="scoringControl" :checked="criterion.scoringControl === 'select' || criterion.scoringControl === null"/>
              {{ lang.get('scoring-criteria.form.select_control.label') }}
            </label>
          </div>
          <div class="radio">
            <label for="sliderScoringControl">
              <input type="radio" id="sliderScoringControl" name="scoringControl" value="slider" v-model="scoringControl" :checked="criterion.scoringControl === 'slider'"/>
              {{ lang.get('scoring-criteria.form.slider_control.label') }}
            </label>
          </div>
          <div class="radio">
            <label for="inputScoringControl">
              <input type="radio" id="inputScoringControl" name="scoringControl" value="input" v-model="scoringControl" :checked="criterion.scoringControl === 'input'"/>
              {{ lang.get('scoring-criteria.form.input_control.label') }}
            </label>
          </div>
        </div>
  
        <div :class="['form-group', {error: hasError('scoringPrecision')}]" id="scoring-precision" v-show="scoringControl === 'input'">
          {{ lang.get('scoring-criteria.form.scoring_precision.label') }}
          <div class="radio">
            <label for="integerPrecision">
              <input type="radio" id="integerPrecision" name="scoringPrecision" value="0" v-model="scoringPrecision" :checked="scoringPrecision == '1' || scoringPrecision === null"/>
              {{ lang.get('scoring-criteria.form.scoring_precision.integer.label') }}
            </label>
          </div>
          <div class="radio">
            <label for="oneDecimalPrecision">
              <input type="radio" id="oneDecimalPrecision" name="scoringPrecision" value="1" v-model="scoringPrecision" :checked="scoringPrecision == '0.1'"/>
              {{ lang.get('scoring-criteria.form.scoring_precision.one-decimal.label') }}
            </label>
          </div>
          <div class="radio">
            <label for="twoDecimalsPrecision">
              <input type="radio" id="twoDecimalsPrecision" name="scoringPrecision" value="2" v-model="scoringPrecision" :checked="scoringPrecision == '0.01'"/>
              {{ lang.get('scoring-criteria.form.scoring_precision.two-decimals.label') }}
            </label>
          </div>
        </div>
  
        <div :class="['form-group', {error: hasError('scoringIncrement')}]" v-show="scoringControl !== 'input'">
          <label for="scoringIncrement">{{ lang.get('scoring-criteria.form.scoring_increment.label') }}</label>
          <input type="text" id="scoringIncrement" name="scoringIncrement" v-model="scoringIncrement" class="form-control">
        </div>

        <input v-if="criterion.id > 0" type="hidden" name="updatedAt" :value="criterion.updatedAt">
      </div>

      <div class="col-xs-12 col-md-4">
        <component-preview :heading="lang.get('scoring-criteria.preview.heading')">
          <div class="form-group">
            <h3 v-if="label.length > 0">
              {{ label }}
              <helpIcon :content="helpText" v-if="helpText.length > 0"></helpIcon>
            </h3>
            <div class="row">
              <div class="col-xs-12">
                <div class="scoring-criterion">
                  <div class="scoring-control">
                    <scoring-control-input
                        v-if="scoringControl === 'input'"
                        :max-score="maximumScore"
                    ></scoring-control-input>
                    <scoring-control-select
                        v-else-if="scoringControl === 'select'"
                        :min-score="minimumScore"
                        :max-score="maximumScore"
                        :increment="scoringIncrement"
                    ></scoring-control-select>
                    <scoring-control-slider
                        v-else
                        :min-score="minimumScore"
                        :max-score="maximumScore"
                        :increment="scoringIncrement"
                        :label="lang.get('miscellaneous.slider.current_value')"
                    ></scoring-control-slider>
                  </div>
                  <div class="scoring-hint-text" v-if="hintText.length > 0">
                    <p>{{ hintText }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </component-preview>
      </div>
    </div>
  </div>
</template>

<script>

import ScoreSetSelector from "../../../lib/components/Selectors/ScoreSetSelector";
import Multilingual from "../../../lib/components/Translations/Multilingual";
import HelpIcon from "../../../lib/components/Shared/HelpIcon";
import CategoryConfig from "../../../lib/components/Categories/CategoryConfig";
import {mapState, mapGetters} from "vuex";
import ComponentPreview from "../../../lib/components/ComponentPreview";
import ScoringControlInput from "./ScoringControlInput";
import ScoringControlSelect from "./ScoringControlSelect";
import ScoringControlSlider from "./ScoringControlSlider";

export default {
  components :{
    ScoreSetSelector,
    CategoryConfig,
    Multilingual,
    HelpIcon,
    ComponentPreview,
    ScoringControlInput,
    ScoringControlSelect,
    ScoringControlSlider
  },
  inject: ['lang'],
  props: {
    scoreSets: {
      type: Array,
      required: true
    },
    categories: {
      type: Array,
      required: true
    },
    criterion: {
      type: Object,
      required: true,
    },
    supportedLanguages: {
      type: Array,
      required: true
    },
    fields: {
      type: Object,
      required: true
    }
  },
  data() {
    const translations = this.criterion.translated[this.lang.locale];

    let obj = {
      categoryOption: this.criterion.categoryOption,
      scoresetId: this.criterion.scoresetId,
      scoringControl: this.criterion.scoringControl || 'select',
      selectedFieldId: this.criterion.fieldId,
      selectedCategories: this.criterion.categories,
      maximumScore: this.criterion.maximumScore ? this.criterion.maximumScore : 10,
      minimumScore: this.criterion.minimumScore ? this.criterion.minimumScore : 0,
      scoringIncrement: this.criterion.scoringIncrement ? this.criterion.scoringIncrement : 1,
      scoringPrecision: this.criterion.scoringPrecision ? this.criterion.scoringPrecision : '0',
      weight: this.criterion.weight,
      label: translations !== undefined ? translations.label : '',
      helpText: translations !== undefined ? translations.helpText : '',
      hintText: translations !== undefined ? translations.hintText : '',
    };

    const formKeys = Object.keys(obj)

    obj.resource = this.criterion

    return {...obj, formKeys: formKeys};
  },
  computed: {
    ...mapState('global', ['validationErrors']),
    ...mapGetters('global', ['hasError']),
    selectedField() {
      return this.fields[this.selectedFieldId];
    }
  },
  mounted() {
    if (! (this.criterion.id > 0)) {
      this.criterion.translated = {}
    }
      this.supportedLanguages.forEach(lang => {
        if (this.resource.translated[lang] === undefined) {
          this.resource.translated[lang] = {
            title: '',
            label: '',
            shortcode: '',
            helpText: '',
            hintText: '',
          };
        }
      })

    this.formKeys.forEach(key => {
      this.$watch(key, (newVal, oldVal) => {
        if (newVal !== oldVal) {
          this.$emit('input', key, newVal)
        }
      })
    })
  },
  methods: {
    showField(field) {
      return field.categories === true || this.selectedCategories.length === 0 || this.selectedCategories.some(categoryId => field.categories.indexOf(categoryId) !== -1)
    },
    onCategorySelected(data) {
      if (data.categoryOption === 'select') {
        this.selectedCategories = data.categories;
      }
    },
    handleMultilingualInput(e, language, property, value) {
      this.resource.translated[language][property] = value;
      this.$emit('input', 'translated.'+property+'.'+language, value)
      
      if (language === this.lang.locale) {
        switch (property) {
          case 'label':
            this.label = value;
            break;
          case 'hintText':
            this.hintText = value;
            break;
          case 'helpText':
            this.helpText = value;
            break;
          default:
        }
      }
    }
  }
}
</script>
